import { takeEvery } from "redux-saga/effects";
import api from "../../../utils/apiService";
import { ENDPOINT } from "../../../config/endpoints";
import { METHODS } from "../../../data/constants";
import { postToLocalServerMaster } from "../../../helpers/localServerClient";
import {
  callLocalServerMaster,
  callSyncStart,
  callSyncStop,
  fetchServerSaleData,
  syncSale,
} from "../../slices/LocalServerSlice";
import { getLocalStorage } from "../../../utils/localStorage";
import { DEVICEIDENTIFIER } from "../../../utils/constants";
import { pos_errormap } from "../../../utils/posErrors";
import { notifyError } from "../../../effects-bridge";
import { callSyncStartApi, callSyncStopApi } from "./shared";

export * from "./shared";

/**
 * WEB half of `redux-store/saga/localServer`. `shared.ts` owns the two identical workers.
 *
 * ⚠️ Every core import names the VARIANT, not the folder (hazard 6/16). `ToastNotifyError`
 * becomes `notifyError` from the effects bridge — the established substitution (`saga/discount`
 * already does this).
 */

/**
 * Post a message to the master terminal over the LAN.
 *
 * ⚠️ **Divergent: this platform SURFACES failures, the other silently swallows them.** A response
 * carrying a `code` is shown through the error toast, and a thrown error is shown too. On the
 * other platform both of those lines are commented out in the app source, so a failed master call
 * produces no user-visible feedback at all. Preserved as-is on both sides.
 *
 * ⚠️ `notifyError(error)` is passed the raw error OBJECT, not a string, exactly as the app did.
 */
function* callLocalMasterAPi(action: any): Generator<any, void, any> {
  let payload = action.payload;
  try {
    const response = yield postToLocalServerMaster({
      serverAddress: payload.serverAddress,
      path: payload.path,
      deviceIdentifier: getLocalStorage(DEVICEIDENTIFIER),
      message: payload.dataToSend,
    });
    console.log("master response here ", response);
    if (response.code) {
      notifyError(pos_errormap[response.code]);
    } else {
      payload.callback(response);
    }
  } catch (error) {
    console.log("error", error);
    notifyError(error as any);
  }
}

/**
 * Push a sale through the master terminal.
 *
 * 🔴 **Divergent, and it matters to callers:** on failure this platform still invokes
 * `payload.callback({ code: "UI_GENERIC_MESSAGE" })`, so the caller learns the sync failed. The
 * other platform's `catch` only logs — its caller's callback never fires at all.
 */
function* callSaleSyncProcess(action: any): Generator<any, void, any> {
  let payload = action.payload;
  try {
    const response = yield api.request({
      method: "POST",
      data: payload.data,
      url: ENDPOINT.SALESYNC.syncProcess(),
    });
    yield payload.callback(response);
  } catch (error) {
    payload.callback({ code: "UI_GENERIC_MESSAGE" });
    console.log("error", error);
  }
}

/**
 * Fetch a sale from the master terminal.
 *
 * 🔴 **Divergent HTTP METHOD.** This platform issues a GET (via `METHODS.GET`); the other issues
 * a POST to the same endpoint builder. Preserved exactly — this is the app's own behaviour on
 * each side, and "fixing" either would change what the master server receives.
 */
function* fetchServerSale(action: any): Generator<any, void, any> {
  let payload = action.payload;
  try {
    const response = yield api.request({
      method: METHODS.GET,
      url: ENDPOINT.SALESYNC.fetchSale(payload.mid),
    });
    payload.callback(response);
  } catch (error) {
    console.log("error", error);
  }
}

export default function* root(): Generator<any, void, any> {
  yield takeEvery(callLocalServerMaster.type, callLocalMasterAPi);
  yield takeEvery(callSyncStart.type, callSyncStartApi);
  yield takeEvery(syncSale.type, callSaleSyncProcess);
  yield takeEvery(callSyncStop.type, callSyncStopApi);
  yield takeEvery(fetchServerSaleData.type, fetchServerSale);
}
